import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { useState } from 'react'
import { PopupState } from 'material-ui-popup-state/hooks'
import { toast } from 'sonner'
import Toast from '../Toast'
import { AxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { TextField } from '@common/components/Form/TextField'
import { HTTPValidationError } from '@common/config/api/client'
import { UsePatchTaskMutation } from '@features/reader_task_list_iam_view/pages/hooks/UsePatchTaskMutation'
import { MoveIcon } from '@common/components/Icons'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: 1,
}

export default function MoveProgressModal({
  task_id,
  study_id,
  popupState,
}: {
  task_id: string
  study_id: string
  popupState: PopupState
}) {
  const [open, setOpen] = useState(false)
  const { control, watch } = useForm({ mode: 'onChange', defaultValues: { reason: '' } })
  const reason = watch('reason')
  const handleClose = () => {
    setOpen(false)
    popupState.close()
  }
  const { mutateAsync: moveTask } = UsePatchTaskMutation()

  const handleMoveTask = async (task_ids: string[], study_id: string, reason: string) => {
    await moveTask({
      body: {
        status: 'IN_PROGRESS',
        status_change_reason: reason === '' ? null : reason,
      },
      query: {
        study_id: study_id,
        read_task_ids: task_ids,
      },
    })
      .then(() => {
        toast(<Toast message="Your request was sent successfully" variant="success" data-testid="success-toast" />)
        handleClose()
      })
      .catch((error: AxiosError<HTTPValidationError>) => {
        const errorMessage = Array.isArray(error.response?.data?.detail)
          ? error.response?.data?.detail.map((err) => err.msg).join(', ')
          : error.response?.data?.detail || error.message
        toast(<Toast message={errorMessage} variant="error" data-testid="error-toast" />)
      })
  }

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
        }}
        data-testid="move-task-button"
        tabIndex={0}
      >
        <MoveIcon />
        <Typography variant="body1">Move to «In progress»</Typography>
      </Box>
      <Modal
        open={open}
        data-testid="move-modal"
        aria-labelledby="move-modal-title"
        aria-describedby="move-modal-description"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              alignSelf: 'stretch',
              width: 600,
            }}
          >
            <Typography variant="h6" color="text.primary" id="move-modal-title">
              Move to «In progress»
            </Typography>
            <Typography variant="body1" color="text.secondary" id="move-modal-description">
              The task will be added back to the reader’s list as «In Progress».
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              padding: 2,
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              name="reason"
              control={control}
              id="outlined-multiline-static"
              label="Provide a reason"
              multiline
              rows={6}
              fullWidth
              onKeyDown={(e) => e.stopPropagation()}
              data-testid="reason-textfield"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              alignSelf: 'stretch',
              padding: 2,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="cancel-move-task-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              onClick={() => handleMoveTask([task_id], study_id, reason)}
              data-testid="confirm-move-task-button"
            >
              Transfer
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
